// frontend/src/pages/customer/OrderDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiArrowLeft,
  FiPackage,
  FiClock,
  FiCheckCircle,
  FiTruck,
  FiXCircle,
  FiCreditCard,
  FiMapPin,
  FiPhone,
  FiFileText,
  FiAlertCircle,
  FiCopy,
  FiCheck
} from 'react-icons/fi';

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [copiedTransactionId, setCopiedTransactionId] = useState(false);

  useEffect(() => {
    fetchOrderDetail();
  }, [id]);

  const fetchOrderDetail = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/customer/orders/${id}`);

      if (response.data.success) {
        setOrder(response.data.data.order);
      }
    } catch (error) {
      console.error('Fetch order detail error:', error);
      toast.error('Захиалгын мэдээлэл татахад алдаа гарлаа');
      navigate('/customer/orders');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!window.confirm('Та төлбөр шилжүүлсэн бол "Тийм" дарна уу')) {
      return;
    }

    setConfirmingPayment(true);

    try {
      const response = await axios.put(`/api/customer/orders/${id}/confirm-payment`);

      if (response.data.success) {
        toast.success('Төлбөрийн мэдээлэл илгээгдлээ! Админ баталгаажуулах болно.');
        fetchOrderDetail(); // Reload order
      }
    } catch (error) {
      console.error('Confirm payment error:', error);
      toast.error(error.response?.data?.message || 'Төлбөр баталгаажуулахад алдаа гарлаа');
    } finally {
      setConfirmingPayment(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedTransactionId(true);
    toast.success('Хуулагдлаа!');
    setTimeout(() => setCopiedTransactionId(false), 2000);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: FiClock, label: 'Хүлээгдэж буй' },
      accepted: { bg: 'bg-blue-100', text: 'text-blue-800', icon: FiCheckCircle, label: 'Баталгаажсан' },
      prepared: { bg: 'bg-purple-100', text: 'text-purple-800', icon: FiPackage, label: 'Бэлтгэгдсэн' },
      dispatched: { bg: 'bg-indigo-100', text: 'text-indigo-800', icon: FiTruck, label: 'Хүргэлтэнд гарсан' },
      delivered: { bg: 'bg-green-100', text: 'text-green-800', icon: FiCheckCircle, label: 'Хүргэгдсэн' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: FiXCircle, label: 'Цуцлагдсан' }
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${badge.bg} ${badge.text}`}>
        <Icon size={16} />
        {badge.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const badges = {
      unpaid: { bg: 'bg-red-100', text: 'text-red-800', label: 'Төлөөгүй' },
      partial: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Хэсэгчлэн' },
      paid: { bg: 'bg-green-100', text: 'text-green-800', label: 'Төлсөн' },
      refunded: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Буцаасан' }
    };

    const badge = badges[paymentStatus] || badges.unpaid;

    return (
      <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-lg mb-4"></div>
          <p className="text-gray-600">Уншиж байна...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container-custom py-12 text-center">
        <p className="text-gray-600">Захиалга олдсонгүй</p>
        <Link to="/customer/orders" className="btn btn-primary mt-4">
          Буцах
        </Link>
      </div>
    );
  }

  const showConfirmPaymentButton = 
  order.payment_method === 'bank_transfer' && 
  order.payment_status === 'unpaid' &&
  order.status !== 'cancelled' &&
  order.transaction?.status !== 'pending_verification';

  return (
    <div className="container-custom py-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/customer/orders"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <FiArrowLeft />
          <span>Буцах</span>
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Захиалга #{order.order_number}
            </h1>
            <p className="text-gray-600 mt-1">
              {new Date(order.created_at).toLocaleString('mn-MN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>

          <div className="flex flex-col sm:items-end gap-2">
            {getStatusBadge(order.status)}
            {getPaymentStatusBadge(order.payment_status)}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
            {/* ✅ Баталгаажуулалт хүлээж байна */}
            {order.payment_method === 'bank_transfer' && 
            order.payment_status === 'unpaid' &&
            order.transaction?.status === 'pending_verification' && (
            <div className="card bg-blue-50 border-blue-200">
                <div className="flex items-start gap-3">
                <FiClock className="text-blue-600 mt-1 flex-shrink-0" size={24} />
                <div className="flex-1">
                    <h3 className="font-semibold text-blue-900 mb-2">
                    Баталгаажуулалт хүлээж байна
                    </h3>
                    <p className="text-sm text-blue-800 mb-3">
                    Та төлбөр шилжүүлснээ мэдэгдсэн. Админ таны төлбөрийг шалгаж баталгаажуулах болно.
                    </p>
                    
                    {/* Төлбөрийн мэдээлэл дахин харуулах */}
                    {order.transaction && (
                    <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-2">Төлбөрийн мэдээлэл:</p>
                        <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                            <span className="text-gray-600">Банк:</span>
                            <span className="font-medium">{order.transaction.bank_name}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Данс:</span>
                            <span className="font-medium">{order.transaction.account_number}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Гүйлгээний утга:</span>
                            <span className="font-mono font-semibold text-blue-600">
                            {order.transaction.transaction_id}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Дүн:</span>
                            <span className="font-semibold text-gray-900">
                            ₮{order.total_amount.toLocaleString()}
                            </span>
                        </div>
                        </div>
                    </div>
                    )}
                </div>
                </div>
            </div>
            )}
          {/* Payment Warning */}
          {showConfirmPaymentButton && (
            <div className="card bg-yellow-50 border-yellow-200">
              <div className="flex items-start gap-3">
                <FiAlertCircle className="text-yellow-600 mt-1 flex-shrink-0" size={24} />
                <div className="flex-1">
                  <h3 className="font-semibold text-yellow-900 mb-2">
                    Төлбөрийн мэдээлэл
                  </h3>
                  <p className="text-sm text-yellow-800 mb-4">
                    Дараах данс руу {order.total_amount.toLocaleString()}₮ шилжүүлээд, 
                    гүйлгээний утга дээр <strong>{order.transaction?.transaction_id}</strong> гэж бичнэ үү.
                  </p>

                  {order.transaction && (
                    <div className="bg-white rounded-lg p-4 mb-4">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Банк:</span>
                          <span className="font-semibold text-gray-900">
                            {order.transaction.bank_name}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Дансны дугаар:</span>
                          <span className="font-semibold text-gray-900">
                            {order.transaction.account_number}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Нэр:</span>
                          <span className="font-semibold text-gray-900">
                            {order.transaction.account_name}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t">
                          <span className="text-gray-600">Гүйлгээний утга:</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold text-blue-600">
                              {order.transaction.transaction_id}
                            </span>
                            <button
                              onClick={() => copyToClipboard(order.transaction.transaction_id)}
                              className="p-1 hover:bg-gray-100 rounded"
                              title="Хуулах"
                            >
                              {copiedTransactionId ? (
                                <FiCheck className="text-green-600" size={16} />
                              ) : (
                                <FiCopy className="text-gray-600" size={16} />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleConfirmPayment}
                    disabled={confirmingPayment}
                    className="btn btn-primary w-full"
                  >
                    {confirmingPayment ? (
                      <>
                        <div className="spinner-sm mr-2"></div>
                        Илгээж байна...
                      </>
                    ) : (
                      <>
                        <FiCheckCircle className="mr-2" />
                        Төлбөр төлсөн
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Order Items */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Захиалсан бараа
            </h3>

            <div className="space-y-4">
              {order.items && order.items.map(item => (
                <div key={item.id} className="flex gap-4 pb-4 border-b last:border-0">
                  <Link
                    to={`/customer/products/${item.product_id}`}
                    className="flex-shrink-0"
                  >
                    <img
                      src={item.image_url ? `${process.env.REACT_APP_API_URL}${item.image_url}` : '/placeholder.png'}
                      alt={item.product_name}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                  </Link>

                  <div className="flex-1">
                    <Link
                      to={`/customer/products/${item.product_id}`}
                      className="font-semibold text-gray-900 hover:text-blue-600 block"
                    >
                      {item.product_name}
                    </Link>
                    <p className="text-sm text-gray-500 mt-1">
                      {item.quantity} × ₮{item.unit_price.toLocaleString()}
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      ₮{item.subtotal.toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Delivery Info */}
          {order.delivery_address && (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Хүргэлтийн мэдээлэл
              </h3>

              <div className="space-y-3">
                {order.delivery_address && (
                  <div className="flex items-start gap-3">
                    <FiMapPin className="text-gray-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-500">Хаяг</p>
                      <p className="text-gray-900">{order.delivery_address}</p>
                    </div>
                  </div>
                )}

                {order.delivery_phone && (
                  <div className="flex items-start gap-3">
                    <FiPhone className="text-gray-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-500">Утас</p>
                      <p className="text-gray-900">{order.delivery_phone}</p>
                    </div>
                  </div>
                )}

                {order.delivery_note && (
                  <div className="flex items-start gap-3">
                    <FiFileText className="text-gray-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-500">Тэмдэглэл</p>
                      <p className="text-gray-900">{order.delivery_note}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Order Summary */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Захиалгын дүн
            </h3>

            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Дүн:</span>
                <span className="font-medium">₮{order.subtotal.toLocaleString()}</span>
              </div>

              {order.discount_rate > 0 && (
                <>
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Хөнгөлөлт ({order.discount_rate}%):</span>
                    <span>-₮{order.discount_amount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Хөнгөлөлтийн дараа:</span>
                    <span className="font-medium">
                      ₮{(order.subtotal - order.discount_amount).toLocaleString()}
                    </span>
                  </div>
                </>
              )}

              <div className="flex justify-between text-sm">
                <span className="text-gray-600">НӨАТ:</span>
                <span className="font-medium">
                  {order.tax > 0 ? `₮${order.tax.toLocaleString()}` : 'Чөлөөлөгдсөн'}
                </span>
              </div>

              <div className="pt-3 border-t flex justify-between">
                <span className="font-semibold text-gray-900">Нийт:</span>
                <span className="text-xl font-bold text-blue-600">
                  ₮{order.total_amount.toLocaleString()}
                </span>
              </div>

              {order.paid_amount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Төлсөн:</span>
                  <span className="font-semibold">₮{order.paid_amount.toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>

          {/* Payment Info */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Төлбөрийн мэдээлэл
            </h3>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Төлбөрийн хэлбэр:</span>
                <span className="font-medium text-gray-900">
                  {order.payment_method === 'bank_transfer' && 'Шилжүүлэг'}
                  {order.payment_method === 'credit' && 'Зээл'}
                  {order.payment_method === 'cash' && 'Бэлэн'}
                  {order.payment_method === 'card' && 'Карт'}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Төлбөрийн статус:</span>
                <span>{getPaymentStatusBadge(order.payment_status)}</span>
              </div>
            </div>
          </div>

          {/* Order Status Timeline */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Захиалгын төлөв
            </h3>

            <div className="space-y-4">
              <div className={`flex items-center gap-3 ${
                order.status === 'pending' ? 'text-yellow-600' : 'text-gray-400'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  order.status === 'pending' ? 'bg-yellow-100' : 'bg-gray-100'
                }`}>
                  <FiClock size={16} />
                </div>
                <span className="text-sm font-medium">Хүлээгдэж буй</span>
              </div>

              <div className={`flex items-center gap-3 ${
                ['accepted', 'prepared', 'dispatched', 'delivered'].includes(order.status)
                  ? 'text-blue-600'
                  : 'text-gray-400'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  ['accepted', 'prepared', 'dispatched', 'delivered'].includes(order.status)
                    ? 'bg-blue-100'
                    : 'bg-gray-100'
                }`}>
                  <FiCheckCircle size={16} />
                </div>
                <span className="text-sm font-medium">Баталгаажсан</span>
              </div>

              <div className={`flex items-center gap-3 ${
                ['prepared', 'dispatched', 'delivered'].includes(order.status)
                  ? 'text-purple-600'
                  : 'text-gray-400'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  ['prepared', 'dispatched', 'delivered'].includes(order.status)
                    ? 'bg-purple-100'
                    : 'bg-gray-100'
                }`}>
                  <FiPackage size={16} />
                </div>
                <span className="text-sm font-medium">Бэлтгэгдсэн</span>
              </div>

              <div className={`flex items-center gap-3 ${
                ['dispatched', 'delivered'].includes(order.status)
                  ? 'text-indigo-600'
                  : 'text-gray-400'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  ['dispatched', 'delivered'].includes(order.status)
                    ? 'bg-indigo-100'
                    : 'bg-gray-100'
                }`}>
                  <FiTruck size={16} />
                </div>
                <span className="text-sm font-medium">Хүргэлтэнд гарсан</span>
              </div>

              <div className={`flex items-center gap-3 ${
                order.status === 'delivered' ? 'text-green-600' : 'text-gray-400'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  order.status === 'delivered' ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  <FiCheckCircle size={16} />
                </div>
                <span className="text-sm font-medium">Хүргэгдсэн</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;